import { computed } from 'vue';
import { useCaseSettings } from '@/stores/caseSettings/store';
import { useSurgicalGuideCase } from '@/stores/surgicalGuide/caseStore';
import { useSurgicalGuideOrders } from '@/stores/surgicalGuideOrders/store';
import { SurgicalGuideOrderState } from '@/stores/surgicalGuideOrders/types';
import { isCRNumberValid } from '@/stores/surgicalGuide/surgicalGuideValidation';
import { useUserStore } from '@/stores/userStore';

const ORDER_STATUS_LABELS: Partial<Record<SurgicalGuideOrderState, string>> = {
    [SurgicalGuideOrderState.Requested]: 'Pending CR number',
    [SurgicalGuideOrderState.Cancelled]: 'Guide Cancelled',
    [SurgicalGuideOrderState.Ordered]: 'Guide Ordered',
    [SurgicalGuideOrderState.Accepted]: 'Guide Ordered',
    [SurgicalGuideOrderState.Rejected]: 'Guide Ordered',
};

export function orderStatusLabel(state: SurgicalGuideOrderState | undefined): string {
    return (state && ORDER_STATUS_LABELS[state]) ?? 'No Guide Order';
}

export type OrderInfoPill = '' | 'enter-cr' | 'requested' | 'ordered';

/** A case still needs a CR number when none is saved or the saved one is invalid. */
export function isCrNumberMissing(crNumber: string): boolean {
    return crNumber === '' || !isCRNumberValid(crNumber);
}

/**
 * The info pill to show under the order button (or '' for none).
 *
 * `enter-cr` is a persistent call to action — anyone who can edit the CR number
 * must add one to an order that still lacks it. It's driven by the case's CR
 * value (loaded with the case, before the dialog opens), so it shows whenever an
 * order exists without a valid CR. The other two are one-off confirmations shown
 * only right after the order was placed.
 */
export function orderInfoPillVariant(
    hasOrder: boolean,
    crNumberMissing: boolean,
    canEditCrNumber: boolean,
    isNewOrder: boolean,
): OrderInfoPill {
    if (!hasOrder) return '';
    if (crNumberMissing && canEditCrNumber) return 'enter-cr';
    if (!isNewOrder) return '';
    return crNumberMissing ? 'requested' : 'ordered';
}

/**
 * Per-plan surgical-guide order display — stateless getters keyed by planId.
 *
 * Holds no state of its own (everything derives from the orders / case stores),
 * so it is a plain composable, not a store. The plan card uses it for status;
 * opening the dialog lives in the separate `useSurgicalGuideOrderDialog` store.
 */
export function usePlanOrderDisplay() {
    const caseSettings = useCaseSettings();
    const caseSurgicalGuide = useSurgicalGuideCase();
    const orders = useSurgicalGuideOrders();
    const userStore = useUserStore();

    return {
        showOrderGuide: computed(() => caseSettings.study?.sourceModality === 'ct-scan'),
        hasOrder: (planId: number) => orders.getPlanOrder(planId) !== null,
        orderStatus: (planId: number) => orderStatusLabel(orders.getPlanOrder(planId)?.state),
        orderInfoPill: (planId: number) =>
            orderInfoPillVariant(
                orders.getPlanOrder(planId) !== null,
                isCrNumberMissing(caseSurgicalGuide.current.crNumber),
                userStore.canEditCrNumber,
                orders.newOrderPlanId === planId,
            ),
    };
}